import { useEffect, useState } from "react";
import { AlertTriangle, Clock, Trash2, Wrench } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [newHoliday, setNewHoliday] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  // Attendance-only reset state
  const [attScope, setAttScope] = useState("today");
  const [attFrom, setAttFrom] = useState("");
  const [attTo, setAttTo] = useState("");
  const [attEmployee, setAttEmployee] = useState("");
  const [employees, setEmployees] = useState([]);
  const [attOpen, setAttOpen] = useState(false);
  const [attBusy, setAttBusy] = useState(false);
  // Maintenance
  const [fixTsBusy, setFixTsBusy] = useState(false);
  const [fixTsResult, setFixTsResult] = useState(null);
  const [maintLog, setMaintLog] = useState([]);

  const loadMaintLog = () =>
    api.get("/admin/maintenance-log?limit=15")
      .then(({ data }) => setMaintLog(data || []))
      .catch(() => {});

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
    api.get("/employees").then(({ data }) => setEmployees(data || [])).catch(() => {});
    loadMaintLog();
  }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setSettings(data);
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Ayarlar</h1>
        <p className="text-muted-foreground text-sm mt-1">Mesai saatleri ve resmi tatiller</p>
      </div>

      <div className="glass-card rounded-xl p-6 space-y-4">
        <div className="font-display font-semibold tracking-tight">Mesai Saatleri ve Günleri</div>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="space-y-1.5">
            <Label>Başlangıç</Label>
            <Input
              value={settings.work_start}
              onChange={(e) => setSettings({ ...settings, work_start: e.target.value })}
              placeholder="08:30"
              data-testid="work-start"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Bitiş</Label>
            <Input
              value={settings.work_end}
              onChange={(e) => setSettings({ ...settings, work_end: e.target.value })}
              placeholder="18:30"
              data-testid="work-end"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Mesai Günleri</Label>
          <div className="flex flex-wrap gap-2" data-testid="work-days">
            {[["Pzt", 0], ["Sal", 1], ["Çar", 2], ["Per", 3], ["Cum", 4], ["Cmt", 5], ["Paz", 6]].map(([label, idx]) => {
              const days = settings.work_days ?? [0, 1, 2, 3, 4, 5];
              const active = days.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`work-day-${idx}`}
                  onClick={() => {
                    const next = active ? days.filter((d) => d !== idx) : [...days, idx].sort((a, b) => a - b);
                    setSettings({ ...settings, work_days: next });
                  }}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:border-primary/50"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground">
            Seçili olmayan günlerde giriş-çıkış kaydedilir ancak geç kalma, erken çıkış ve fazla mesai hesaplanmaz.
          </div>
        </div>
        <Button
          onClick={() => save({
            work_start: settings.work_start,
            work_end: settings.work_end,
            work_days: settings.work_days ?? [0, 1, 2, 3, 4, 5],
          })}
          data-testid="save-hours"
        >
          Kaydet
        </Button>
      </div>

      <div className="glass-card rounded-xl p-6 space-y-4">
        <div className="font-display font-semibold tracking-tight">QR Okutma Cooldown</div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Personel bir kez okuttuktan sonra tekrar okutmadan önce geçmesi gereken süre.
          Kısa aralıklı çift okutmayı ve yanlış çıkış kayıtlarını engeller.
        </p>
        <div className="max-w-xs">
          <Label className="mb-1.5 block">Süre (saniye)</Label>
          <Input
            type="number"
            min={0}
            value={settings.scan_cooldown_seconds ?? 60}
            onChange={(e) => setSettings({ ...settings, scan_cooldown_seconds: parseInt(e.target.value) || 0 })}
            data-testid="cooldown-seconds"
          />
          <div className="text-xs text-muted-foreground mt-1">Devre dışı bırakmak için 0 girin.</div>
        </div>
        <Button
          onClick={() => save({ scan_cooldown_seconds: settings.scan_cooldown_seconds })}
          data-testid="save-cooldown"
        >
          Kaydet
        </Button>
      </div>

      <div className="glass-card rounded-xl p-6 space-y-4">
        <div className="font-display font-semibold tracking-tight">Resmi Tatiller</div>
        <div className="flex gap-2 max-w-md">
          <Input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} data-testid="new-holiday" />
          <Button
            onClick={() => {
              if (!newHoliday) return;
              save({ holidays: [...(settings.holidays || []), newHoliday] });
              setNewHoliday("");
            }}
          >Ekle</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(settings.holidays || []).map((h) => (
            <span key={h} className="px-3 py-1 rounded-full bg-secondary text-sm flex items-center gap-2">
              {h}
              <button
                onClick={() => save({ holidays: settings.holidays.filter((x) => x !== h) })}
                className="text-muted-foreground hover:text-red-500"
              >×</button>
            </span>
          ))}
          {(settings.holidays || []).length === 0 && <div className="text-xs text-muted-foreground">Kayıt yok.</div>}
        </div>
      </div>

      <div className="rounded-xl p-6 space-y-3 border-2 border-blue-500/30 bg-blue-500/5" data-testid="maintenance">
        <div className="flex items-center gap-2 font-display font-semibold tracking-tight text-blue-500">
          <Wrench className="w-5 h-5" /> Bakım
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Eski kayıtlarda zaman dilimi hatası vardı — kaydedilen saat
          <code className="mx-1 px-1 py-0.5 rounded bg-secondary/60 text-xs font-mono">+00:00</code>
          eki taşıyordu ve tarayıcıda 3 saat fazla gösteriyordu. Bu buton, eski kayıtlardaki
          zaman damgalarını düzeltir. Yeni kayıtlar zaten doğru formattadır; işlem
          güvenle birden fazla kez çalıştırılabilir.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={async () => {
              setFixTsBusy(true);
              setFixTsResult(null);
              try {
                const { data } = await api.post("/admin/fix-timestamps");
                setFixTsResult(data);
                toast.success(
                  `${data.fixed_check_in + data.fixed_check_out} kayıt düzeltildi (${data.scanned} tarandı)`
                );
                loadMaintLog();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail));
              } finally { setFixTsBusy(false); }
            }}
            disabled={fixTsBusy}
            className="gap-2 border-blue-500/40 text-blue-500 hover:bg-blue-500/10 hover:text-blue-500"
            data-testid="fix-timestamps"
          >
            <Wrench className={`w-4 h-4 ${fixTsBusy ? "animate-spin" : ""}`} />
            {fixTsBusy ? "Onarılıyor..." : "Zaman Damgalarını Onar"}
          </Button>
          {fixTsResult && (
            <div className="text-xs font-mono text-muted-foreground" data-testid="fix-timestamps-result">
              {fixTsResult.scanned} tarandı · {fixTsResult.fixed_check_in} giriş · {fixTsResult.fixed_check_out} çıkış düzeltildi
            </div>
          )}
        </div>

        {maintLog.length > 0 && (
          <div className="mt-4 border-t border-border/40 pt-4" data-testid="maintenance-log">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Son Bakım İşlemleri
            </div>
            <div className="divide-y divide-border/30 max-h-64 overflow-y-auto rounded-md bg-secondary/30">
              {maintLog.map((row) => {
                const labels = {
                  fix_timestamps: "Zaman Damgası Onarımı",
                  reset_attendance: "Giriş-Çıkış Verisi Silme",
                  reset_data: "Tüm Veri Sıfırlama",
                };
                const label = labels[row.action] || row.action;
                let detail = "";
                if (row.action === "fix_timestamps") {
                  detail = `${row.meta?.scanned} tarandı · ${row.meta?.fixed_check_in} giriş · ${row.meta?.fixed_check_out} çıkış düzeltildi`;
                } else if (row.action === "reset_attendance") {
                  detail = `Kapsam: ${row.meta?.scope} · ${row.meta?.attendance_deleted} kayıt · ${row.meta?.logs_deleted} log silindi`;
                } else if (row.action === "reset_data") {
                  const total = Object.values(row.meta?.cleared || {}).reduce((a, b) => a + b, 0);
                  detail = `${total} kayıt (tüm koleksiyonlar) silindi`;
                }
                return (
                  <div key={row.id} className="p-3 text-xs flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground">{label}</div>
                      <div className="text-muted-foreground font-mono mt-0.5 truncate">{detail}</div>
                    </div>
                    <div className="text-right text-muted-foreground font-mono shrink-0">
                      <div>{new Date(row.created_at).toLocaleString("tr-TR")}</div>
                      <div className="opacity-70">{row.actor_email}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl p-6 space-y-3 border-2 border-amber-500/40 bg-amber-500/5" data-testid="attendance-reset">
        <div className="flex items-center gap-2 font-display font-semibold tracking-tight text-amber-500">
          <Clock className="w-5 h-5" /> Giriş-Çıkış Verilerini Temizle
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Sadece <strong>puantaj / giriş-çıkış (attendance)</strong> kayıtlarını siler.
          Personel, izinler, admin hesapları ve genel ayarlar <strong>korunur</strong>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
          <div className="space-y-1.5">
            <Label>Kapsam</Label>
            <Select value={attScope} onValueChange={setAttScope}>
              <SelectTrigger data-testid="att-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today" data-testid="att-scope-today">Sadece Bugün</SelectItem>
                <SelectItem value="range" data-testid="att-scope-range">Tarih Aralığı</SelectItem>
                <SelectItem value="employee" data-testid="att-scope-employee">Belirli Personel</SelectItem>
                <SelectItem value="all" data-testid="att-scope-all">Tümü (dikkat)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {attScope === "range" && (
            <>
              <div className="space-y-1.5">
                <Label>Başlangıç</Label>
                <Input type="date" value={attFrom} onChange={(e) => setAttFrom(e.target.value)} data-testid="att-from" />
              </div>
              <div className="space-y-1.5">
                <Label>Bitiş</Label>
                <Input type="date" value={attTo} onChange={(e) => setAttTo(e.target.value)} data-testid="att-to" />
              </div>
            </>
          )}

          {attScope === "employee" && (
            <div className="space-y-1.5 md:col-span-2">
              <Label>Personel</Label>
              <Select value={attEmployee} onValueChange={setAttEmployee}>
                <SelectTrigger data-testid="att-employee">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.first_name} {e.last_name} · #{e.employee_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Button
          variant="outline"
          onClick={() => setAttOpen(true)}
          disabled={
            (attScope === "range" && !attFrom && !attTo) ||
            (attScope === "employee" && !attEmployee)
          }
          className="gap-2 border-amber-500/40 text-amber-500 hover:bg-amber-500/10 hover:text-amber-500"
          data-testid="open-att-reset"
        >
          <Trash2 className="w-4 h-4" /> Giriş-Çıkış Verilerini Sil
        </Button>
      </div>

      <div className="rounded-xl p-6 space-y-3 border-2 border-red-500/40 bg-red-500/5" data-testid="danger-zone">
        <div className="flex items-center gap-2 font-display font-semibold tracking-tight text-red-500">
          <AlertTriangle className="w-5 h-5" /> Tehlikeli Bölge
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Bu işlem <strong>tüm personel kayıtlarını, giriş-çıkışları, izinleri, bildirimleri ve logları</strong> siler.
          Admin hesapları ve genel ayarlar korunur. Bu işlem <strong>geri alınamaz</strong>.
        </p>
        <Button
          variant="destructive"
          onClick={() => { setResetOpen(true); setResetConfirm(""); }}
          data-testid="open-reset"
          className="gap-2"
        >
          <AlertTriangle className="w-4 h-4" /> Tüm Verileri Sıfırla
        </Button>
      </div>

      <Dialog open={attOpen} onOpenChange={setAttOpen}>
        <DialogContent data-testid="att-reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-500 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Giriş-Çıkış Verileri Silinsin mi?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              Aşağıdaki kapsamdaki puantaj kayıtları kalıcı olarak silinecek:
            </div>
            <div className="rounded-lg bg-secondary/70 p-3 space-y-1 text-xs font-mono">
              {attScope === "today" && <div>Kapsam: <b>Bugün</b></div>}
              {attScope === "all" && <div>Kapsam: <b>TÜM giriş-çıkış kayıtları</b></div>}
              {attScope === "range" && (
                <div>Kapsam: <b>{attFrom || "…"} → {attTo || "…"}</b></div>
              )}
              {attScope === "employee" && (
                <div>Kapsam: <b>{employees.find((e) => e.id === attEmployee)?.first_name} {employees.find((e) => e.id === attEmployee)?.last_name}</b> (tüm günler)</div>
              )}
            </div>
            <div className="text-muted-foreground">
              Personel bilgileri, izinler ve ayarlar korunacak. Bu işlem geri alınamaz.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttOpen(false)}>İptal</Button>
            <Button
              disabled={attBusy}
              onClick={async () => {
                setAttBusy(true);
                try {
                  const params = new URLSearchParams({ scope: attScope });
                  if (attScope === "range") {
                    if (attFrom) params.set("from_date", attFrom);
                    if (attTo) params.set("to_date", attTo);
                  } else if (attScope === "employee") {
                    params.set("employee_id", attEmployee);
                  }
                  const { data } = await api.post(`/admin/reset-attendance?${params.toString()}`);
                  toast.success(
                    `${data.attendance_deleted} kayıt silindi (${data.logs_deleted} log temizlendi)`
                  );
                  setAttOpen(false);
                  loadMaintLog();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                } finally { setAttBusy(false); }
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="att-reset-confirm"
            >
              {attBusy ? "Siliniyor..." : "Evet, Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Verileri Sıfırla
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>Bu işlem geri alınamaz. Silinecekler:</div>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Tüm personel kayıtları ve QR/PIN kodları</li>
              <li>Tüm giriş-çıkış kayıtları</li>
              <li>Tüm izin talepleri</li>
              <li>Tüm bildirimler ve log kayıtları</li>
            </ul>
            <div>Onaylamak için aşağıya <span className="font-mono font-bold text-red-500">SIFIRLA</span> yazın:</div>
            <Input
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="SIFIRLA"
              className="font-mono"
              data-testid="reset-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>İptal</Button>
            <Button
              variant="destructive"
              disabled={resetConfirm !== "SIFIRLA" || resetting}
              onClick={async () => {
                setResetting(true);
                try {
                  const { data } = await api.post("/admin/reset-data");
                  const total = Object.values(data.cleared).reduce((a, b) => a + b, 0);
                  toast.success(`${total} kayıt silindi. Sistem sıfırlandı.`);
                  setResetOpen(false);
                  setResetConfirm("");
                  loadMaintLog();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                } finally { setResetting(false); }
              }}
              data-testid="reset-confirm-btn"
            >
              {resetting ? "Siliniyor..." : "Evet, Sıfırla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
